import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert
} from '@mui/material';
import { Add, Edit, Delete, MedicalServices, LocalHospital, Warning, FamilyRestroom, Medication, History } from '@mui/icons-material';
import { useDataContext } from '../contexts/DataContext';
import { formatDateForDateInput, formatDateInputToISO } from '../utils/dateUtils';

const MedicalHistoryList = ({ patientId, onUpdate }) => {
  const { medicalHistory: allHistory, addMedicalHistory, updateMedicalHistory, deleteMedicalHistory } = useDataContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingHistory, setEditingHistory] = useState(null);
  const [categories, setCategories] = useState([
    { value: 'allergies', label: 'Allergies' },
    { value: 'chronic_diseases', label: 'Maladies chroniques' },
    { value: 'surgeries', label: 'Chirurgies' },
    { value: 'hospitalizations', label: 'Hospitalisations' },
    { value: 'family_history', label: 'Antécédents familiaux' },
    { value: 'medications', label: 'Médicaments' },
    { value: 'other', label: 'Autre' }
  ]);
  const [severityLevels, setSeverityLevels] = useState([
    { value: 'mild', label: 'Léger' },
    { value: 'moderate', label: 'Modéré' },
    { value: 'severe', label: 'Sévère' },
    { value: 'critical', label: 'Critique' }
  ]);
  const [statusOptions, setStatusOptions] = useState([
    { value: 'active', label: 'Actif' },
    { value: 'resolved', label: 'Résolu' },
    { value: 'chronic', label: 'Chronique' },
    { value: 'unknown', label: 'Inconnu' }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    severity: 'moderate',
    status: 'active',
    notes: ''
  });

  const categoryIcons = {
    allergies: <Warning color="error" />,
    chronic_diseases: <MedicalServices color="primary" />,
    surgeries: <LocalHospital color="secondary" />,
    hospitalizations: <LocalHospital color="info" />,
    family_history: <FamilyRestroom color="success" />,
    medications: <Medication color="warning" />,
    other: <History color="default" />
  };

  const severityColors = {
    mild: 'success',
    moderate: 'warning',
    severe: 'error',
    critical: 'error'
  };

  const statusColors = {
    active: 'error',
    resolved: 'success',
    chronic: 'warning',
    unknown: 'default'
  };

  // Filtrer les antécédents pour ce patient
  const histories = allHistory.filter(h => h.patientId === patientId);

  const handleOpenDialog = (history = null) => {
    if (history) {
      setEditingHistory(history);
      setFormData({
        category: history.category || '',
        title: history.title || '',
        description: history.description || '',
        start_date: formatDateForDateInput(history.start_date),
        end_date: formatDateForDateInput(history.end_date),
        severity: history.severity || 'moderate',
        status: history.status || 'active',
        notes: history.notes || ''
      });
    } else {
      setEditingHistory(null);
      setFormData({
        category: '',
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        severity: 'moderate',
        status: 'active',
        notes: ''
      });
    }
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingHistory(null);
    setFormData({
      category: '',
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      severity: 'moderate',
      status: 'active',
      notes: ''
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.category || !formData.title) {
      setError('La catégorie et le titre sont requis');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const historyData = {
        ...formData,
        patientId: patientId,
        history_type: formData.category,
        record_date: formData.start_date ? formatDateInputToISO(formData.start_date) : new Date().toISOString(),
        start_date: formData.start_date ? formatDateInputToISO(formData.start_date) : null,
        end_date: formData.end_date ? formatDateInputToISO(formData.end_date) : null
      };

      if (editingHistory) {
        await updateMedicalHistory(editingHistory.id, historyData);
      } else {
        await addMedicalHistory(historyData);
      }

      handleCloseDialog();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Une erreur est survenue lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (historyId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet antécédent ?')) {
      try {
        await deleteMedicalHistory(historyId);
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Erreur lors de la suppression');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifié';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getCategoryLabel = (categoryValue) => {
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  const getSeverityLabel = (severityValue) => {
    const severity = severityLevels.find(sev => sev.value === severityValue);
    return severity ? severity.label : severityValue;
  };

  const getStatusLabel = (statusValue) => {
    const status = statusOptions.find(stat => stat.value === statusValue);
    return status ? status.label : statusValue;
  };

  // Trier les antécédents par date de création (plus récents en premier)
  const sortedHistories = histories.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  
  // Grouper les antécédents par catégorie
  const groupedHistories = sortedHistories.reduce((acc, history) => {
    if (!acc[history.category]) {
      acc[history.category] = [];
    }
    acc[history.category].push(history);
    return acc;
  }, {});

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Antécédents Médicaux</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Ajouter un antécédent
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {Object.keys(groupedHistories).length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary" align="center">
              Aucun antécédent médical enregistré
            </Typography>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedHistories).map(([category, categoryHistories]) => (
          <Box key={category} mb={3}>
            <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
              {categoryIcons[category]}
              {getCategoryLabel(category)}
            </Typography>
            {categoryHistories.map((history) => (
              <Card key={history.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Typography variant="h6" gutterBottom>
                        {history.title}
                      </Typography>
                      {history.description && (
                        <Typography variant="body2" color="textSecondary" paragraph>
                          {history.description}
                        </Typography>
                      )}
                      <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                        <Chip
                          label={getSeverityLabel(history.severity)}
                          color={severityColors[history.severity]}
                          size="small"
                        />
                        <Chip
                          label={getStatusLabel(history.status)}
                          color={statusColors[history.status]}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Début:</strong> {formatDate(history.start_date)} | 
                        <strong> Fin:</strong> {formatDate(history.end_date)}
                      </Typography>
                      {history.notes && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>Notes:</strong> {history.notes}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(history)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(history.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ))
      )}

      {/* Dialog pour ajouter/modifier un antécédent */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingHistory ? 'Modifier l\'antécédent' : 'Nouvel antécédent'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Catégorie"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Titre *"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de début"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de fin"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sévérité</InputLabel>
                <Select
                  value={formData.severity}
                  onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                  label="Sévérité"
                >
                  {severityLevels.map((severity) => (
                    <MenuItem key={severity.value} value={severity.value}>
                      {severity.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Statut"
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Sauvegarde...' : (editingHistory ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalHistoryList;
